// src/pages/Contact.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaEnvelope, FaLinkedin, FaGithub, FaArrowRight, FaMapMarkerAlt } from 'react-icons/fa';
import { useLang } from '../contexts/LanguageContext';

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7rem 2rem 5rem;
  background: ${(props) => props.theme.gradients.background};
  min-height: 100vh;
  position: relative;
  overflow: hidden;
`;

const GlowOrb = styled.div<{ $color: string; $top: string; $left: string; $size: string }>`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$size};
  height: ${(p) => p.$size};
  background: ${(p) => p.$color};
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.2;
  pointer-events: none;
`;

const SectionLabel = styled(motion.div)`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.accent};
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.22);
  padding: 0.35rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.2rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 1rem;
  text-align: center;

  span {
    background: ${(props) => props.theme.gradients.accent};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subtitle = styled(motion.p)`
  color: ${(props) => props.theme.colors.light};
  font-size: 1.05rem;
  max-width: 520px;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const AvailabilityBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1.4rem;
  background: rgba(0, 230, 118, 0.07);
  border: 1px solid rgba(0, 230, 118, 0.25);
  border-radius: 50px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #00e676;
  margin-bottom: 3rem;
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  background: #00e676;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.5); }
    50% { opacity: 0.85; box-shadow: 0 0 0 6px rgba(0, 230, 118, 0); }
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 860px;
  margin-bottom: 2.5rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    max-width: 380px;
  }
`;

const ContactCard = styled(motion.a)`
  background: #0e0e12;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 20px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.35s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, #7c3aed, #a78bfa);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(139, 92, 246, 0.3);
    box-shadow: 0 0 0 1px rgba(139,92,246,0.12), 0 25px 50px rgba(0,0,0,0.45);
    &::before { opacity: 1; }
  }
`;

const CardIconWrap = styled.div<{ $color: string }>`
  width: 58px;
  height: 58px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.45rem;
  background: ${(p) => p.$color}15;
  border: 1px solid ${(p) => p.$color}30;
  color: ${(p) => p.$color};
  transition: all 0.3s ease;

  ${ContactCard}:hover & {
    transform: scale(1.08);
  }
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.white};
`;

const CardValue = styled.p`
  font-size: 0.8rem;
  color: ${(props) => props.theme.colors.light};
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const BottomCard = styled(motion.div)`
  background: #0e0e12;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 24px;
  padding: 2.5rem 2.5rem;
  width: 100%;
  max-width: 580px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const LocationRow = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: ${(props) => props.theme.colors.muted};
  margin-bottom: 1rem;
`;

const BottomMessage = styled.p`
  color: ${(props) => props.theme.colors.light};
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.9rem 2rem;
  background: linear-gradient(135deg, #7c3aed, #9b6cfc);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 24px rgba(124, 58, 237, 0.35);
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 40px rgba(124, 58, 237, 0.5);
  }
`;

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.12 },
  }),
};

const Contact: React.FC = () => {
  const { t } = useLang();

  const contactItems = [
    { href: 'mailto:igorchrzyptowicz@gmail.com', icon: <FaEnvelope />, color: '#a78bfa', title: t.contact.emailLabel,    value: 'igorchrzyptowicz@gmail.com', external: false },
    { href: 'https://www.linkedin.com/in/igor-chrzyptowicz-3183911b7/', icon: <FaLinkedin />, color: '#0a66c2', title: t.contact.linkedinLabel, value: 'Igor Chrzyptowicz',           external: true  },
    { href: 'https://github.com/IgorLTS10', icon: <FaGithub />, color: '#e6edf3', title: t.contact.githubLabel,   value: '@IgorLTS10',                 external: true  },
  ];

  return (
    <ContactContainer id="contact">
      <GlowOrb $color="rgba(139,92,246,0.3)" $top="-5%" $left="68%" $size="420px" />
      <GlowOrb $color="rgba(124,58,237,0.2)" $top="50%" $left="3%"  $size="360px" />

      <SectionLabel
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t.contact.sectionLabel}
      </SectionLabel>

      <Title
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {t.contact.title} <span>{t.contact.titleHighlight}</span>
      </Title>

      <Subtitle
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25, duration: 0.6 }}
      >
        {t.contact.subtitle}
      </Subtitle>

      <AvailabilityBadge
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.35 }}
      >
        <PulseDot />
        {t.contact.available}
      </AvailabilityBadge>

      <ContactGrid>
        {contactItems.map((item, i) => (
          <ContactCard
            key={item.title}
            href={item.href}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noopener noreferrer' : undefined}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <CardIconWrap $color={item.color}>{item.icon}</CardIconWrap>
            <CardTitle>{item.title}</CardTitle>
            <CardValue>{item.value}</CardValue>
          </ContactCard>
        ))}
      </ContactGrid>

      <BottomCard
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <LocationRow>
          <FaMapMarkerAlt size={12} /> {t.contact.location}
        </LocationRow>
        <BottomMessage>{t.contact.message}</BottomMessage>
        <PrimaryButton href="mailto:igorchrzyptowicz@gmail.com">
          {t.contact.emailCta} <FaArrowRight size={13} />
        </PrimaryButton>
      </BottomCard>
    </ContactContainer>
  );
};

export default Contact;
